import { Card, Grid, Icon, IconButton, InputLabel, Skeleton, Tooltip } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { MDNoSelectedClient } from "components/MDNoSelectedClient";
import MDSelect from "components/MDSelect";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { toDate, toDateReqFormat } from "utils/DateUtils";
import { post } from "utils/WebService";
import IndiceCreateEdit from "./IndiceCreateEdit";
import UploadArquivoIndice from "./UploadArquivoIndice";
import { ESCURO } from "assets/coresTema";

export default function IndiceConsult() {

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const today = new Date();

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const formattedStartDate = toDateReqFormat(firstDayOfMonth, 'yyyy-MM-dd');
    const formattedEndDate = toDateReqFormat(lastDayOfMonth, 'yyyy-MM-dd');

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
        dataInicio: formattedStartDate,
        dataFim: formattedEndDate,
        tipoIndice: null
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);
    const [uploadArquivo, setUploadArquivo] = useState(false);
    const [appliedFilter, setAppliedFilter] = useState(false);
    const [showTable, setShowTable] = useState(false);

    const [filters, setFilters] = useState({
        dataInicio: formattedStartDate,
        dataFim: formattedEndDate,
        tipoIndice: null
    });

    const { tipoIndiceEnum } = useUser();
    const tipoIndiceEnumWithAll = [{ id: null, name: "Todos", code: "todos" }, ...tipoIndiceEnum];

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered, tipoIndice, dateInit, dateEnd }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
            dataInicio: dateInit ? dateInit : tableState.current.dataInicio,
            dataFim: dateEnd ? dateEnd : tableState.current.dataFim,
            tipoIndice: (tipoIndice || tipoIndice?.code == "todos" || tipoIndice == 0) ? tipoIndice?.id : tableState.current.tipoIndice
        };

        tableState.current = updatedTableState;

        post("/indice/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
            dataInicio: updatedTableState.dataInicio,
            dataFim: updatedTableState.dataFim,
            tipoIndice: updatedTableState.tipoIndice,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });

    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
        setUploadArquivo(false);
    }

    const userColumns = [
        {
            header: "tipo",
            accessorKey: "tipo",
            cell: info => {
                const status = info.getValue();
                const statusName = tipoIndiceEnum?.find(ht => ht.code === status);
                return <DefaultCell>{statusName ? statusName.name : "-"}</DefaultCell>
            },
            isSorted: true
        },
        {
            header: "valor",
            accessorKey: "value",
            cell: info => <DefaultCell>{info.getValue().toString()}</DefaultCell>,
            isSorted: true
        },
        {
            header: "data",
            accessorKey: "date",
            cell: info => <DefaultCell>{toDate(info.getValue()) || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        setAppliedFilter(true);
        setAplicar(true);
        setShowTable(true);
        fetchData({
            ...tableState, pageIndex: 0, tipoIndice: filters?.tipoIndice,
            dateInit: filters?.dataInicio, dateEnd: filters?.dataFim
        });
    };

    const handleCleanFilter = () => {
        setAppliedFilter(false);
        setShowTable(false);
        setFilters({
            dataInicio: formattedStartDate,
            dataFim: formattedEndDate,
            tipoIndice: null
        })
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    return (
        <Grid container display="flex" justifyContent="center">

            {uploadArquivo &&
                <Grid item xs={12} md={12}>
                    <UploadArquivoIndice
                        closeCreateEdit={backFunction} />
                </Grid>
            }

            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <IndiceCreateEdit
                        id={selectedId}
                        closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: (isCreateEdit || uploadArquivo) ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo de Índice</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.code}
                                    getOptionLabel={(option) => option.name}
                                    name="status"
                                    placeholder="Selecione"
                                    value={filters.tipoIndice || null}
                                    options={tipoIndiceEnumWithAll}
                                    onChange={(selectedOption) => {
                                        setFilters({ ...filters, tipoIndice: selectedOption })
                                    }}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Início</InputLabel>
                                <MDTextFieldCalendar
                                    type="date"
                                    value={filters.dataInicio || ""}
                                    fullWidth
                                    onChange={(event) => {
                                        setFilters({ ...filters, dataInicio: event.target.value })
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    darkMode={darkMode}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Fim</InputLabel>
                                <MDTextFieldCalendar
                                    type="date"
                                    value={filters.dataFim || ""}
                                    fullWidth
                                    onChange={(event) => {
                                        setFilters({ ...filters, dataFim: event.target.value })
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    darkMode={darkMode}
                                />
                            </Grid>
                             <Grid item xs={12} display='flex' justifyContent='space-between' pt={1}>
                                <MDBox display="flex" gap={2}>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={() => {
                                            setSelectedId(null);
                                            setIsCreateEdit(true);
                                        }}>
                                        Adicionar índice
                                    </MDButton>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        onClick={() => {
                                            setUploadArquivo(true);
                                        }}>
                                        UPLOAD ARQUIVO
                                    </MDButton>
                                </MDBox>

                                <MDBox display="flex">
                                    <MDButton
                                        size="medium"
                                        sx={{ marginRight: 2 }}
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleCleanFilter}>
                                        Limpar filtro
                                    </MDButton>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleApplyFilter}>
                                        Aplicar filtro
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        </Grid>
                </Card>
                {(filters.tipoIndice == null || !appliedFilter) ?
                    <MDNoSelectedClient wasWanted={false} isCase isIndice />
                    :
                    (!showTable ?
                        <Skeleton variant="rectangular" height={pxToRem(300)} sx={{ borderRadius: 1 }} />
                        :
                        <DataTable
                            loading={loadingTable}
                            columnData={userColumns}
                            data={data}
                            fetchData={fetchData}
                            totalPages={pages}
                            totalRecords={recordsTotal}
                            tableState={tableState}
                            changeFilter={aplicar} />
                    )
                }
            </Grid>
        </Grid >
    );
}

